export interface Data {
    // Define the structure of your data fetched from the API
    // Modify this according to your API response structure
    id: number;
    name: string;
    datetime: string;
    author: {
        id: number;
        name: string;
    };
    snippet: Record<string, any>;
    // Add more properties as needed
}

interface DataResponse {
    count: number;
    results: Data[];
}

const LIMIT = 500;
const baseURL = new URL(`https://api.getwemap.com/v3.0/livemaps/search?limit=${LIMIT}`);
const DB_ID = 'livemapSnippets';
const DB_STORE_NAME = 'snippetStore';

async function getAllLivemaps(onProgress?: (progress: number) => void): Promise<Data[]> {
    const totalResults: Data[] = [];

    let count = 0;
    let offset = 0;

    do {
        baseURL.searchParams.set('offset', offset.toString());
        console.log('Fetching', baseURL.toString());
        const response: DataResponse = await fetch(baseURL).then((resp) => resp.json());
        count = count || response.count;

        totalResults.push(...response.results);

        if (onProgress) {
            onProgress(totalResults.length / count);
        }

        offset += LIMIT;
    } while (totalResults.length < count);

    const uniqueResults = totalResults.filter((item, index, self) => self.findIndex((i) => i.id === item.id) === index);

    return uniqueResults;
}

export async function getLivemapsFromDB(): Promise<Data[]> {
    return new Promise<Data[]>((resolve, reject) => {
        const request = window.indexedDB.open(DB_ID, 1);

        request.onerror = (event) => {
            console.error('IndexedDB error:', (event.target as IDBOpenDBRequest).error);
            reject();
        };

        request.onsuccess = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            const transaction = db.transaction(DB_STORE_NAME, 'readonly');
            const objectStore = transaction.objectStore(DB_STORE_NAME);
            const getRequest = objectStore.getAll();
            getRequest.onsuccess = (event) => {
                resolve((event.target as IDBRequest<Data[]>).result);
            };
        };
    });
}

export async function initializeDB({
    forcePopulate = false,
    onProgress = () => {}
}: {
    forcePopulate?: boolean;
    onProgress?: (progress: number) => void
} = {}) {
    if (!('indexedDB' in window)) {
        console.error('IndexedDB is not supported.');
        return;
    }

    return new Promise<Data[]>((resolve, reject) => {
        const request = window.indexedDB.open(DB_ID, 1);

        request.onerror = (event) => {
            console.error('IndexedDB error:', (event.target as IDBOpenDBRequest).error);
            reject();
        };

        request.onupgradeneeded = (event) => {
            console.log('onupgradeneeded');
            const objectStore = (event.target as IDBOpenDBRequest).result.createObjectStore(DB_STORE_NAME, { keyPath: 'id' });
            objectStore.createIndex("id", "id", { unique: true });
            objectStore.createIndex("datetime", "datetime", { unique: false });
        };

        request.onsuccess = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;

            const populateDB = () => {
                getAllLivemaps(onProgress).then((newData) => {
                    const transactionAdd = db.transaction(DB_STORE_NAME, 'readwrite');
                    const objectStoreAdd = transactionAdd.objectStore(DB_STORE_NAME);
                    const addRequest = objectStoreAdd.clear();
                    console.log('1 - Populate DB with ', newData.length, 'items.');
                    addRequest.onsuccess = () => {
                        newData.forEach((item) => {
                            item.datetime = new Date().toISOString();
                            objectStoreAdd.add(item);
                        });
                    };

                    objectStoreAdd.transaction.oncomplete = () => {
                        resolve(newData);
                        console.log('IndexedDB populated with', newData.length, 'items.');
                    }

                    objectStoreAdd.transaction.onerror = () => {
                        console.error('Transaction error:', objectStoreAdd.transaction.error);
                    }
                });
            }

            const transaction = db.transaction(DB_STORE_NAME, 'readwrite');
            const objectStore = transaction.objectStore(DB_STORE_NAME);
            // Check if IndexedDB is already populated
            const getRequest = objectStore.getAll();
            getRequest.onsuccess = (event) => {
                const existingData = (event.target as IDBRequest<Data[]>).result;

                if (existingData.length === 0 || forcePopulate) {
                    // If IndexedDB is empty, save data
                    populateDB();
                } else {
                    // Check the last update time
                    const lastUpdateTime = new Date(existingData[existingData.length - 1].datetime);
                    const currentTime = new Date();
                    const sevenDaysAgo = new Date(currentTime.getTime() - 7 * 24 * 60 * 60 * 1000);

                    if (lastUpdateTime < sevenDaysAgo) {
                        populateDB();
                    } else {
                        resolve(existingData);
                        console.log('IndexedDB is already populated and updated within 7 days.', existingData.length, 'items.');
                    }
                }
            };

            transaction.onerror = () => {
                console.error('Transaction error:', transaction.error);
                reject();
            };
        };
    });
}
